import { PointVector2D } from "./geometry";
import PerlinNoise3D from "./noise";
import { lerp, range } from "./lerp";
import { isUndefined } from "util";

export class Flowfield {
    cellsX: number;
    cellsY: number;
    xSize: number;
    ySize: number;
    cellSizeX: number;
    cellSizeY: number;
    charge: number;
    time: number;
    noise: PerlinNoise3D;
    vectors: Array<Array<PointVector2D>>;
    chargesUpdated: boolean;
    edgesToCenter: boolean;
    particles: Array<Particle>

    constructor(cellsX: number, cellsY: number, xSize: number, ySize: number, charge = 1) {
        this.cellsX = cellsX;
        this.cellsY = cellsY;
        this.xSize = xSize;
        this.ySize = ySize;
        this.cellSizeX = this.xSize / this.cellsX;
        this.cellSizeY = this.ySize / this.cellsY;
        this.charge = charge;
        this.time = 0;
        this.noise = new PerlinNoise3D(-360, 360, 0.1);
        this.vectors = [];
        this.particles = [];
        this.chargesUpdated = false;
        this.edgesToCenter = false;
        for (let i = 0; i < this.cellsY; i++) {
            const xVectorArray = [];
            for (let j = 0; j < this.cellsX; j++) {
                xVectorArray.push(new PointVector2D(1, 0));
            }
            this.vectors.push(xVectorArray);
        }
    }

    updateCellSizes() {
        this.cellSizeX = this.xSize / this.cellsX;
        this.cellSizeY = this.ySize / this.cellsY;
    }

    setTime(time: number) {
        if (this.time != time) {
            this.time = time;
            this.chargesUpdated = false;
        }
    }

    setEdgesToCenter(edgesToCenter = true) {
        this.edgesToCenter = edgesToCenter;
    }

    setFieldSize(xSize: number, ySize: number) {
        this.xSize = xSize;
        this.ySize = ySize;
        this.updateCellSizes();
    }

    setFieldCells(cellsX: number, cellsY: number) {
        if (this.cellsX > cellsX) this.vectors.forEach((value) => value.slice(cellsX));
        else if (this.cellsX < cellsX) this.vectors.forEach((value) => {
            for (let i = 0; i < cellsX - this.cellsX; i++) value.push(value[this.cellsX - 1].copy());
        });
        if (this.cellsY > cellsY) this.vectors.slice(cellsX);
        else if (this.cellsY < cellsY) {
            for (let i = 0; i < cellsY - this.cellsY; i++) {
                const xVectorArray = [];
                for (let j = 0; j < cellsX; j++) {
                    xVectorArray.push(this.vectors[this.cellsY - 1][j].copy());
                }
                this.vectors.push(xVectorArray);
            }
        }
        this.cellsX = cellsX;
        this.cellsY = cellsY;
        this.updateCellSizes();
        this.chargesUpdated = false;
    }

    updateNoise(scale = 1, octaves = 4, amp_falloff = 0.5, seed = 0) {
        this.noise.noiseDetail(octaves, amp_falloff);
        this.noise.noiseScale(scale);
        this.noise.noiseSeed(seed);
        this.chargesUpdated = false;
    }

    updateAllCharges() {
        this.vectors.forEach((value, indexY) => {
            value.forEach((value, indexX) => this.updateCharge(indexX, indexY));
        });
        this.chargesUpdated = true;
    }

    updateCharge(cellX: number, cellY: number) {
        const charge = this.vectors[cellY][cellX];
        let direction = this.noise.get(cellX, cellY, this.time);
        charge.orient(direction).setMagnitude(this.charge);
        if (this.edgesToCenter) {
            const distFromCenterX = Math.abs(cellX - ((this.cellsX - 1) / 2)) / ((this.cellsX - 1) / 2);
            const distFromCenterY = Math.abs(cellY - ((this.cellsY - 1) / 2)) / ((this.cellsY - 1) / 2);
            const distFromCenter = Math.max(distFromCenterX, distFromCenterY);
            const toCenter = new PointVector2D(this.xSize / 2, this.ySize / 2).subtract(this.getChargeOrigin(cellX, cellY));
            charge.add(toCenter.setMagnitude(this.charge).multiply(distFromCenter * distFromCenter * distFromCenter * 10)).setMagnitude(this.charge);
        }
    }

    getCharge(x: number, y: number) {
        if (x >= this.xSize) x = this.xSize - 1;
        else if (x < 0) x = 0;
        if (y >= this.ySize) y = this.ySize - 1;
        else if (y < 0) y = 0;
        const cellX = Math.floor(x / this.cellSizeX);
        const cellY = Math.floor(y / this.cellSizeY);
        const charge = this.vectors[cellY][cellX];
        if (!this.chargesUpdated) this.updateCharge(cellX, cellY);
        return charge;
    }

    getChargeOrigin(cellX: number, cellY: number) {
        if (cellX > this.cellsX - 1) cellX = this.cellsX - 1;
        else if (cellX < 0) cellX = 0;
        if (cellY > this.cellsY - 1) cellY = this.cellsY - 1;
        else if (cellY < 0) cellY = 0;
        return new PointVector2D(this.cellSizeX * cellX + this.cellSizeX / 2, this.cellSizeY * cellY + this.cellSizeY / 2)
    }

    createParticles(number: number, maxCharge?: number, followCursor?: boolean) {
        for(let i = 0; i < number; i++) {
            this.particles.push(new Particle(this, maxCharge, followCursor));
            this.particles[i].randomizeLocation();
        }
    }

    setParticlesFollowCursor(followCursor: boolean) {
        if(this.particles.length > 0) {
            this.particles.forEach(v => v.setFollowCursor(followCursor));
        }
    }

    moveParticles(fps: number, cursorX?: number, cursorY?: number) {
        if(this.particles.length > 0) {
            this.particles.forEach(v => v.move(fps, cursorX, cursorY));
        }
    }
}

export class Particle {
    flowfield: Flowfield;
    location: PointVector2D;
    velocity: PointVector2D;
    maxCharge: number | undefined;
    followCursor: boolean;
    cursorLocation: PointVector2D;
    
    constructor(flowfield: Flowfield, maxCharge?: number, followCursor = false) {
        this.flowfield = flowfield;
        this.location = new PointVector2D(0, 0);
        this.velocity = new PointVector2D(0, 0);
        this.maxCharge = maxCharge;
        this.followCursor = followCursor;
        this.cursorLocation = new PointVector2D(0,0);
    }

    randomizeLocation() {
        const x = Math.random() * this.flowfield.xSize;
        const y = Math.random() * this.flowfield.ySize;
        this.location.set(x, y);
    }

    setFollowCursor(followCursor: boolean) {
        this.followCursor = followCursor;
    }

    move(fps: number, cursorX?: number, cursorY?: number) {
        if (!this.followCursor || cursorX === undefined || cursorY === undefined) {
            if(!this.location.x || !this.location.y) this.randomizeLocation();
            const charge = this.flowfield.getCharge(this.location.x, this.location.y);
            this.velocity.add(charge);
            if (this.maxCharge && this.velocity.magnitude > this.maxCharge) this.velocity.setMagnitude(this.maxCharge);
            this.location.add(this.velocity);
        }
        else {
            const time = this.flowfield.time;
            this.cursorLocation.set(cursorX, cursorY);
            this.velocity = this.cursorLocation.copy().subtract(this.location);
            this.velocity.setMagnitude(this.velocity.magnitude / 5 * (120 / fps));
            this.location.add(this.velocity);
        }
    }
}